/**
 * NotificationModal - Universal Modal Component for User Interactions
 * 
 * This is the foundational modal component for ALL user interactions requiring
 * a popup window. It replaces browser alerts and serves as the base for any
 * direct user interaction that needs a modal interface.
 * 
 * Key Features:
 * - Four notification types with appropriate styling and icons
 * - Flexible sizing (small, medium, large, xlarge)
 * - Optional header/footer display
 * - Scrollable content support
 * - Keyboard navigation (ESC/Enter)
 * - Backdrop click prevention option
 * - Theme-aware styling
 * - Custom sizing via props
 * 
 * Usage Examples:
 * 
 * // Simple info message
 * <NotificationModal
 *   isOpen={showInfo}
 *   onClose={() => setShowInfo(false)}
 *   title="Information"
 *   message="Your settings have been saved."
 * />
 * 
 * // Large scrollable content modal
 * <NotificationModal
 *   isOpen={showContent}
 *   onClose={() => setShowContent(false)}
 *   title="Terms and Conditions"
 *   size="large"
 *   scrollable={true}
 *   showFooter={false}
 * >
 *   <div>...long scrollable content...</div>
 * </NotificationModal>
 * 
 * // Confirmation dialog
 * <NotificationModal
 *   isOpen={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Confirm Delete"
 *   message="Are you sure you want to delete this item?"
 *   type="warning"
 *   showConfirmButton={true}
 *   confirmButtonText="Delete"
 *   preventBackdropClose={true}
 * />
 * 
 * // Custom content-only modal
 * <NotificationModal
 *   isOpen={showCustom}
 *   onClose={() => setShowCustom(false)}
 *   showHeader={false}
 *   showFooter={false}
 *   size="xlarge"
 *   scrollable={true}
 * >
 *   <CustomFormComponent />
 * </NotificationModal>
 */

import React, { useState } from 'react';
import { Info, CheckCircle, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { useKeyboard } from '../hooks/useKeyboard';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';
export type ModalSize = 'small' | 'medium' | 'large' | 'xlarge';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title?: string;
  message?: string;
  type?: NotificationType;
  size?: ModalSize;
  showHeader?: boolean;
  showFooter?: boolean;
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  scrollable?: boolean;
  maxHeight?: string;
  width?: string;
  preventBackdropClose?: boolean;
  children?: React.ReactNode;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  size = 'medium',
  showHeader = true,
  showFooter = true,
  showCancelButton = true,
  showConfirmButton = false,
  confirmButtonText = 'OK',
  cancelButtonText = 'Close',
  scrollable = false,
  maxHeight,
  width,
  preventBackdropClose = false,
  children
}) => {
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleModalMouseDown = (e: React.MouseEvent) => {
    setMouseDownTarget(e.target);
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // Only close modal if mousedown and mouseup happened on the same overlay target
    if (!preventBackdropClose && e.target === mouseDownTarget && e.target === e.currentTarget) {
      handleClose();
    }
    setMouseDownTarget(null);
  };

  // Global keyboard handling for modal
  useKeyboard({
    onEscape: handleClose,
    onEnter: showConfirmButton ? handleConfirm : handleClose,
    enabled: isOpen,
    enableEnter: true
  });

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={24} />;
      case 'warning':
        return <AlertTriangle size={24} />;
      case 'error':
        return <AlertCircle size={24} />;
      default:
        return <Info size={24} />;
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'modal-small';
      case 'large': return 'modal-large';
      case 'xlarge': return 'modal-xlarge';
      default: return 'modal-medium';
    }
  };

  return (
    <>
      <div 
        className="modal-overlay" 
        onMouseDown={handleModalMouseDown}
        onClick={handleModalClick}
      >
        <div 
          className={`modal-content notification-modal notification-${type} ${getSizeClass()} ${scrollable ? 'scrollable' : ''}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxHeight: maxHeight || (scrollable ? '90vh' : 'auto'),
            width: width || 'auto'
          }}
        >
          {showHeader && (title || showHeader) && (
            <div className="modal-header">
              {type !== 'info' || title ? getIcon() : null}
              {title && <h3>{title}</h3>}
              {!showConfirmButton && (
                <button 
                  className="modal-close-btn"
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          )}
          
          <div className={`modal-body ${scrollable ? 'scrollable-body' : ''}`}>
            {message && <p style={{ whiteSpace: 'pre-line' }}>{message}</p>}
            {children}
          </div>
          
          {showFooter && (showCancelButton || showConfirmButton) && (
            <div className="modal-footer">
              {showCancelButton && (
                <button className="btn btn-secondary" onClick={handleClose}>
                  {cancelButtonText}
                </button>
              )}
              {showConfirmButton && (
                <button 
                  className={`btn btn-${type === 'error' ? 'danger' : 'primary'}`}
                  onClick={handleConfirm}
                >
                  {confirmButtonText}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--bg-overlay);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          box-shadow: var(--shadow-heavy);
          max-height: 90vh;
          overflow: hidden;
          border: 1px solid var(--border-secondary);
          animation: modalSlideIn 0.2s ease-out;
          padding: 0;
        }

        .notification-modal .modal-body {
          padding: 24px;
        }

        @keyframes modalSlideIn {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        .notification-modal {
          width: 90%;
          max-width: 500px;
        }

        .modal-small {
          max-width: 400px;
        }

        .modal-medium {
          max-width: 500px;
        }

        .modal-large {
          max-width: 700px;
        }

        .modal-xlarge {
          max-width: 900px;
        }

        .scrollable {
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }

        .scrollable-body {
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1;
          max-height: calc(90vh - 140px);
        }

        .scrollable-body::-webkit-scrollbar {
          width: 8px;
        }

        .scrollable-body::-webkit-scrollbar-track {
          background: var(--bg-secondary);
          border-radius: 4px;
        }

        .scrollable-body::-webkit-scrollbar-thumb {
          background: var(--border-primary);
          border-radius: 4px;
        }

        .scrollable-body::-webkit-scrollbar-thumb:hover {
          background: var(--text-secondary);
        }

        .notification-info {
          border: 2px solid var(--accent-blue);
          box-shadow: 0 20px 60px rgba(59, 130, 246, 0.2);
        }

        .notification-success {
          border: 2px solid var(--accent-green);
          box-shadow: 0 20px 60px rgba(34, 197, 94, 0.2);
        }

        .notification-warning {
          border: 2px solid var(--accent-orange);
          box-shadow: 0 20px 60px rgba(249, 115, 22, 0.2);
        }

        .notification-error {
          border: 2px solid #dc2626;
          box-shadow: 0 20px 60px rgba(220, 38, 38, 0.2);
        }

        .notification-modal .modal-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 24px;
          border-bottom: 1px solid var(--border-primary);
          background: var(--bg-primary);
          position: relative;
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .notification-info .modal-header {
          border-bottom-color: var(--accent-blue);
          background: rgba(59, 130, 246, 0.05);
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .notification-info .modal-header svg {
          color: var(--accent-blue);
        }

        .notification-info .modal-header h3 {
          color: var(--accent-blue);
        }

        .notification-success .modal-header {
          border-bottom-color: var(--accent-green);
          background: rgba(34, 197, 94, 0.05);
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .notification-success .modal-header svg {
          color: var(--accent-green);
        }

        .notification-success .modal-header h3 {
          color: var(--accent-green);
        }

        .notification-warning .modal-header {
          border-bottom-color: var(--accent-orange);
          background: rgba(249, 115, 22, 0.05);
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .notification-warning .modal-header svg {
          color: var(--accent-orange);
        }

        .notification-warning .modal-header h3 {
          color: var(--accent-orange);
        }

        .notification-error .modal-header {
          border-bottom-color: #dc2626;
          background: rgba(220, 38, 38, 0.05);
          border-top-left-radius: var(--radius-large);
          border-top-right-radius: var(--radius-large);
        }

        .notification-error .modal-header svg {
          color: #dc2626;
        }

        .notification-error .modal-header h3 {
          color: #dc2626;
        }

        .notification-info.notification-modal .modal-footer {
          background: rgba(59, 130, 246, 0.05);
          border-top-color: var(--accent-blue);
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .notification-success.notification-modal .modal-footer {
          background: rgba(34, 197, 94, 0.05);
          border-top-color: var(--accent-green);
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .notification-warning.notification-modal .modal-footer {
          background: rgba(249, 115, 22, 0.05);
          border-top-color: var(--accent-orange);
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .notification-error.notification-modal .modal-footer {
          background: rgba(220, 38, 38, 0.05);
          border-top-color: #dc2626;
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .notification-modal .modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          flex: 1;
        }

        .modal-close-btn {
          background: var(--bg-tertiary);
          border: none;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: var(--border-primary);
        }

        .notification-modal .modal-body p {
          margin-bottom: 16px;
          color: var(--text-primary);
          line-height: 1.5;
        }

        .notification-modal .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          padding: 24px;
          border-top: 1px solid var(--border-primary);
          background: var(--bg-secondary);
          border-bottom-left-radius: var(--radius-large);
          border-bottom-right-radius: var(--radius-large);
        }

        .btn {
          padding: 12px 24px;
          border-radius: var(--radius-medium);
          border: none;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .btn-secondary {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
        }

        .btn-secondary:hover {
          background: var(--border-secondary);
        }

        .btn-primary {
          background: var(--accent-blue);
          color: white;
        }

        .btn-primary:hover {
          opacity: 0.8;
        }

        .btn-danger {
          background: var(--accent-red);
          color: white;
        }

        .btn-danger:hover {
          opacity: 0.8;
        }
      `}</style>
    </>
  );
};